import * as React from 'react';
import { ScaleQuestion } from './ScaleQuestion';
import { BinaryQuestion } from './BinaryQuestion';
import { TextQuestion } from './TextQuestion';
import { PrivacyPolicy } from '../PrivacyPolicy';
import { SurveyInstanceDataService } from '../../services';
import { SurveyAssessmentViewModel, Question, DemographicResponses, AssessmentProgress, AssessmentTimerData, QuestionOption } from '../../models';
import { BaseRouteProps } from '../../interfaces';
import { LoadingIndicator } from '..';
import { Demographics } from './Demographics';
import { detect } from 'detect-browser';
import './Assessment.css';

enum AssessmentSection {
    TrueFalse,
    Likert,
    Text
}

interface AssessmentState {
    assessmentSection: AssessmentSection;
    survey: SurveyAssessmentViewModel;
    assessmentProgress?: AssessmentProgress;
    demographicResponses?: DemographicResponses;
    assessmentTimerData?: AssessmentTimerData;
    questionCount: number;
    surveyLoaded: boolean;
    agreementAccepted: boolean;
    termsOfServiceAccepted: boolean;
    termsOfServiceDeclined: boolean;
    demographicsSubmitted: boolean;
    numberScaleQuestions: number;
    numberBooleanQuestions: number;
    numberTextQuestions: number;
    scaleQuestions: Question[];
    booleanQuestions: Question[];
    textQuestions: Question[];
    booleanQuestionCounter: number;
    scaleQuestionCounter: number;
    textQuestionCounter: number;
    isSubmitting: boolean;
    sectionStartTime: number;
    surveyCompleted: boolean;
    mouseClicks: number;
    showPrivacy: boolean;
}

export class Assessment extends React.Component<BaseRouteProps<{ id: string }>, AssessmentState> {
    private surveyInstanceDataService = new SurveyInstanceDataService(this.props.notificationService);
    private maxQuestionsPerPage = 15;
    private localStorageIdentifer = '';

    private surveyWrapper: any;

    constructor(props: BaseRouteProps<{ id: string }>) {
        super(props);

        this.localStorageIdentifer = `SURVEY_${this.props.match.params.id}`;

        this.state = {
            assessmentSection: AssessmentSection.TrueFalse,
            survey: new SurveyAssessmentViewModel(),
            questionCount: 0,
            surveyLoaded: false,
            agreementAccepted: false,
            termsOfServiceAccepted: false,
            termsOfServiceDeclined: false,
            demographicsSubmitted: false,
            numberBooleanQuestions: 0,
            numberScaleQuestions: 0,
            numberTextQuestions: 0,
            booleanQuestions: [],
            scaleQuestions: [],
            textQuestions: [],
            booleanQuestionCounter: 0,
            scaleQuestionCounter: 0,
            textQuestionCounter: 0,
            isSubmitting: false,
            sectionStartTime: Date.now(),
            surveyCompleted: false,
            mouseClicks: 0,
            showPrivacy: false
        };

        this.next = this.next.bind(this);
        this.submit = this.submit.bind(this);
        this.acceptAgreement = this.acceptAgreement.bind(this);
        this.submitDemographics = this.submitDemographics.bind(this);
        this.acceptToS = this.acceptToS.bind(this);
        this.declineToS = this.declineToS.bind(this);
        this.resetToS = this.resetToS.bind(this);
        this.saveToLocalStorage = this.saveToLocalStorage.bind(this);

        addEventListener('beforeunload', this.saveToLocalStorage);
        // addEventListener('onkeyup', this.onkeyup);
    }

    togglePrivacy() {
        this.setState({
            showPrivacy: !this.state.showPrivacy
        });
    }

    handleKeyPress(e: any) {
        var clicks = this.state.mouseClicks;
        clicks++;
        if (clicks === 10) {
            // fill in all questions with random data
            console.log(this.state.booleanQuestions);
            var booleanQuestions = this.state.booleanQuestions;
            var scaleQuestions = this.state.scaleQuestions;
            
            booleanQuestions.map((q: Question) => {
                if (!q.questionOptions) {
                    return;
                }
                if (Math.random() > 0.5) {
                    q.questionOptions[0].checked = false;
                    q.questionOptions[1].checked = true;
                } else {
                    q.questionOptions[0].checked = true;
                    q.questionOptions[1].checked = false;
                }
            });

            scaleQuestions.map((q: Question) => {
                if (!q.questionOptions) {
                    return;
                }
                q.questionOptions.map((qo: QuestionOption) => {
                    qo.checked = false;
                });
                var ind = Math.floor(Math.random() * q.questionOptions.length);
                q.questionOptions[ind].checked = true;
            });

            this.setState({
                booleanQuestions: booleanQuestions,
                scaleQuestions: scaleQuestions,
                booleanQuestionCounter: booleanQuestions.length,
                scaleQuestionCounter: scaleQuestions.length
            });
        }
        this.setState({
            mouseClicks: clicks
        });
    }

    public componentDidMount() {
        this.surveyInstanceDataService.getByUnique(this.props.match.params.id)
            .then((survey: SurveyAssessmentViewModel) => {
                // let's check if we have saved progress for this survey
                const storedProgress = localStorage.getItem(this.localStorageIdentifer);

                let agreementAccepted = false;
                let demographicsSubmitted = false;
                let demographicResponses = undefined;
                let timerData = undefined;
                let termsOfServiceAccepted = false;
                let termsOfServiceDeclined = false;
                let surveyCompleted = false;

                if (storedProgress) {
                    const progress = JSON.parse(storedProgress) as AssessmentProgress;

                    survey = progress.survey;

                    agreementAccepted = progress.agreementAccepted;
                    demographicsSubmitted = progress.demographicsSubmitted;
                    demographicResponses = progress.demographicResponses;
                    timerData = progress.timerData;
                    termsOfServiceAccepted = progress.termsOfServiceAccepted;
                    termsOfServiceDeclined = progress.termsOfServiceDeclined;
                    surveyCompleted = progress.surveyCompleted;
                }

                // now lets divide up the questions and set the users current state
                var scaleQuestions = survey.questions.filter(x => x.questionType === 'scale');
                var booleanQuestions = survey.questions.filter(x => x.questionType === 'binary');
                var textQuestions = survey.questions.filter(x => x.questionType === 'text' || x.questionType === 'freeText');

                // now let's fine what question they were last on and what page to return them to
                let booleanQuestionCounter = 0;
                let scaleQuestionCounter = 0;
                let textQuestionCounter = 0;
                let completedPages = 0;

                let lastAnswered = 0;
                if (this.checkAllQuestionsAnswered(booleanQuestions).length > 0) {
                    // we're still in the boolean section
                    lastAnswered = this.findIndexOfLastAnswered(booleanQuestions);
                    completedPages = Math.floor(lastAnswered / this.maxQuestionsPerPage) ;
                    booleanQuestionCounter = completedPages * this.maxQuestionsPerPage;
                    this.setState({
                        assessmentSection: AssessmentSection.TrueFalse
                    });
                } else if (this.checkAllQuestionsAnswered(scaleQuestions).length > 0) {
                    // we're still in the boolean section
                    lastAnswered = this.findIndexOfLastAnswered(scaleQuestions);
                    completedPages = Math.floor(lastAnswered / this.maxQuestionsPerPage) ;
                    scaleQuestionCounter = completedPages * this.maxQuestionsPerPage;

                    completedPages = Math.floor(booleanQuestions.length / this.maxQuestionsPerPage) ;
                    textQuestionCounter = completedPages * this.maxQuestionsPerPage;
                    
                    this.setState({
                        assessmentSection: AssessmentSection.Likert
                    });
                } else if (this.checkAllQuestionsAnswered(textQuestions).length > 0) {
                    // we've completed all boolean questions and are now in the scale question section
                    textQuestionCounter = textQuestions.length;
                    lastAnswered = this.findIndexOfLastAnswered(textQuestions);
                    completedPages = Math.floor(lastAnswered / this.maxQuestionsPerPage);
                    textQuestionCounter = completedPages * this.maxQuestionsPerPage;

                    completedPages = Math.floor(scaleQuestions.length / this.maxQuestionsPerPage) ;
                    scaleQuestionCounter = completedPages * this.maxQuestionsPerPage;

                    completedPages = Math.floor(booleanQuestions.length / this.maxQuestionsPerPage) ;
                    textQuestionCounter = completedPages * this.maxQuestionsPerPage;

                    this.setState({
                        assessmentSection: AssessmentSection.Text
                    });
                } else {
                    booleanQuestionCounter =  Math.floor(booleanQuestions.length / this.maxQuestionsPerPage);
                    scaleQuestionCounter = Math.floor(scaleQuestions.length / this.maxQuestionsPerPage);
                    textQuestionCounter = Math.floor(textQuestions.length / this.maxQuestionsPerPage);

                    // all questions have been answered
                    
                    var assessmentSection = AssessmentSection.TrueFalse;

                    if (scaleQuestions.length > 0) {
                        assessmentSection = AssessmentSection.Likert;
                    }

                    if (textQuestions.length > 0) {
                        textQuestionCounter = completedPages * this.maxQuestionsPerPage;
                    }

                    this.setState({
                        assessmentSection: assessmentSection
                    });
                }

                // make sure our timer data is in sync in case they finished the boolean questions and then closed the tab because this would have
                // caused us to not record an end date for the boolean section and a start date for the scale section
                if (booleanQuestionCounter === booleanQuestions.length && lastAnswered === 0) {
                    if (timerData) {
                        timerData.binaryEndDate = new Date();
                        timerData.scaleStartDate = new Date();
                    }
                }

                if (scaleQuestionCounter === scaleQuestions.length && lastAnswered === 0) {
                    if (timerData) {
                        timerData.scaleEndDate = new Date();
                        timerData.textStartDate = new Date();
                    }
                }

                this.setState({
                    survey: survey,
                    surveyLoaded: true,
                    scaleQuestions: scaleQuestions,
                    numberScaleQuestions: scaleQuestions.length,
                    booleanQuestions: booleanQuestions,
                    numberBooleanQuestions: booleanQuestions.length,
                    textQuestions: textQuestions,
                    numberTextQuestions: textQuestions.length,
                    agreementAccepted: agreementAccepted,
                    demographicsSubmitted: demographicsSubmitted,
                    demographicResponses: demographicResponses,
                    assessmentTimerData: timerData,
                    termsOfServiceAccepted: termsOfServiceAccepted,
                    termsOfServiceDeclined: termsOfServiceDeclined,
                    surveyCompleted: surveyCompleted,
                    scaleQuestionCounter: scaleQuestionCounter,
                    booleanQuestionCounter: booleanQuestionCounter,
                    textQuestionCounter: textQuestionCounter
                });
            })
            .catch((error) => {
                this.props.history.push({
                    pathname: '/error',
                    state: error
                });
            });
    }

    public componentWillUpdate(nextProps: Readonly<any>, nextState: Readonly<AssessmentState>, nextContext: any): void {
        if (nextState.survey.completedDate) {
            // survey has been completed, redirect to assessment complete
            this.props.history.push({
                pathname: '/assessmentComplete',
                state: { completedDate: nextState.survey.completedDate }
            });
        } else if (nextState.survey.expired) {
            // survey is expired, redirect
            this.props.history.push('/assessmentExpired');
        }
    }

    public componentDidUpdate() {
        this.surveyWrapper.scrollIntoView(true);
    }

    public componentWillUnmount() {
        removeEventListener('beforeunload', this.saveToLocalStorage);
    }

    public saveToLocalStorage() {
        // only overwrite data if survey information has been loaded and processed from the survey
        // otherwise we can lose progress if the user quickly opens and closes the page.
        if (this.state.surveyLoaded) {
            let { agreementAccepted, termsOfServiceAccepted, termsOfServiceDeclined, demographicsSubmitted, demographicResponses, survey, assessmentTimerData, numberBooleanQuestions, numberScaleQuestions, booleanQuestionCounter, scaleQuestionCounter, sectionStartTime, surveyCompleted } = this.state;

            // if survey isn't marked completed (and we're into the questions section), update elapsed seconds for the section we are in
            if (!surveyCompleted && termsOfServiceAccepted) {
                if (booleanQuestionCounter < numberBooleanQuestions) {
                    let millis = Date.now() - sectionStartTime;
                    assessmentTimerData!.binaryElapsedSeconds += Math.floor(millis / 1000);
                } else if (scaleQuestionCounter < numberScaleQuestions) {
                    let millis = Date.now() - sectionStartTime;
                    assessmentTimerData!.binaryElapsedSeconds += Math.floor(millis / 1000);
                } else {
                    let millis = Date.now() - sectionStartTime;
                    assessmentTimerData!.textElapsedSeconds += Math.floor(millis / 1000);
                }
            }

            const progress: AssessmentProgress = {
                agreementAccepted: agreementAccepted,
                termsOfServiceAccepted: termsOfServiceAccepted,
                termsOfServiceDeclined: termsOfServiceDeclined,
                demographicsSubmitted: demographicsSubmitted,
                demographicResponses: demographicResponses,
                timerData: assessmentTimerData,
                surveyCompleted: surveyCompleted,
                survey: survey
            };

            localStorage.setItem(this.localStorageIdentifer, JSON.stringify(progress));
        }
    }

    private showSubmitButton() {
        const { booleanQuestions, scaleQuestions, textQuestions, 
            textQuestionCounter, scaleQuestionCounter, booleanQuestionCounter, 
            numberTextQuestions, numberScaleQuestions, numberBooleanQuestions } = this.state;

        var booleanSet = this.checkAllQuestionsAnswered(booleanQuestions).length === 0;
        var scaleSet = this.checkAllQuestionsAnswered(scaleQuestions).length === 0;
        var textSet = this.checkAllQuestionsAnswered(textQuestions).length === 0;

        if (booleanSet && scaleSet && textSet) {
            // All questions have been answered
            return true;
        }

        if (booleanSet && scaleSet && textQuestions.length > 0) {
            if (textQuestionCounter + this.maxQuestionsPerPage > numberTextQuestions ) {
                return true;
            }
        } else if (booleanSet && scaleQuestions.length > 0) {
            // There are only boolean & likert questions
            if (textQuestions.length === 0) {
                if (scaleQuestionCounter + this.maxQuestionsPerPage > numberScaleQuestions ) {
                    return true;
                }
            }
        } else {
            // There are only boolean questions
            if (scaleQuestions.length === 0 && textQuestions.length === 0) {
                return booleanQuestionCounter + this.maxQuestionsPerPage > numberBooleanQuestions;
            }
        }

        return false;
    }

    public render() {
        var result;
        var progress = { width: '0%' };

        var binarySection = 1;
        var likertSection = 2;
        var textSection = 3;
        if (this.state.numberBooleanQuestions <= 0) {
            likertSection--;
            textSection--;
        }
        if (this.state.numberScaleQuestions <= 0) {
            textSection--;
        }

        if (!this.state.surveyLoaded) {
            result = <LoadingIndicator />;
        } else if (!this.state.agreementAccepted) {
            // this is the landing page
            var sectionCount = 0;
            if (this.state.numberBooleanQuestions > 0) {
                sectionCount++;
            }
            if (this.state.numberScaleQuestions > 0) {
                sectionCount++;
            }
            if (this.state.numberTextQuestions > 0) {
                sectionCount++;
            }

            result = (
                <div>
                    <img className="survey-logo" src="/images/ponderalogo.png" />
                    <div className="scale-question-wrapper question">
                        <h1>Welcome to the Pondera® Virtual Advisor (PVA) assessment</h1>
                        <hr />
                        <p>This assessment is intended to assist the sponsoring individual or organization that asked you to take the PVA in helping you to be as successful as you can be. You can perform at your best when both you and your sponsor are aware of your preferences and style. The PVA is intended to help discover and communicate these attributes. So get comfortable in a quiet place where you can select responses to a series of statements without interruption. Don't let anyone help you, don't omit any responses and try to complete it all in one period of time.</p>
                        <p>Overall, there are {this.state.numberBooleanQuestions + this.state.numberScaleQuestions + this.state.numberTextQuestions} statements in {sectionCount} sections. 
                            {this.state.numberBooleanQuestions > 0 && <span> Section {binarySection} contains {this.state.numberBooleanQuestions} True/False items.</span>}
                            {this.state.numberScaleQuestions > 0 && <span> Section {likertSection} contains {this.state.numberScaleQuestions} Likert scale items.</span>} 
                            {this.state.numberTextQuestions > 0 && <span> Section {textSection} contains {this.state.numberTextQuestions} Free form text items.</span>} Most people can complete the sections in about 40-50 minutes.</p>
                        <p>When taking the PVA, put yourself in a relaxed frame of mind and try to respond as your "full self" or "true self," rather than how you are solely at work or at home. Work quickly - usually your first response is your best response. Keep in mind that there are no right or wrong responses. These recommendations will help you get results that most accurately reflect who you are as a person.</p>
                        <p>Your responses will {(this.state.numberBooleanQuestions > 0 || this.state.numberScaleQuestions > 0) && <span> be scored and </span>}result in an online report that will be made available to your sponsor. The information provided in this report, in combination with many other factors relating to your knowledge and experience, will help your sponsor better understand the person that is uniquely you. This awareness of your personality and overall style can help others understand you better and contribute to your success in life.</p>
                        <button onClick={this.acceptAgreement} className="btn btn-primary">Continue</button>
                        <div style={{ 'clear': 'both' }} />
                    </div>
                    <div className="copyright-notice">© Pondera Technologies LLC {(new Date()).getFullYear()}</div>
                </div>);
        } else if (!this.state.termsOfServiceAccepted && !this.state.termsOfServiceDeclined) {
            // this is the Terms of Service
            result = (
                <div>
                    <img className="survey-logo" src="/images/ponderalogo.png" />
                    <div className="scale-question-wrapper question">
                        <h1>Terms and Conditions of Use</h1>
                        <hr />
                        <p>The PVA allows an organization to obtain information as to a particular individual's preferences or preferred operating style.  When this information is combined with other available evaluation criteria, including the organization's knowledge of and experience with the individual, it can assist the organization in effectively utilizing the skills, abilities, and strengths of each individual to increase both organizational and individual efficiency and effectiveness.  An organization utilizing this information must carefully evaluate the text, compare it with material from other sources and, in the final analysis, take full responsibility for any action taken.</p>
                        <p>By completing the PVA assessment, you acknowledge and agree that:</p>
                        <ol>
                            <li>Pondera may share the results of your completed assessment with the sponsoring organization;</li>
                            <li>Pondera does not guarantee the accuracy or completeness of any information in or provided in connection with your use of the PVA and is not liable for any course of action or treatment taken by the sponsoring organization;</li>
                            <li>Except as expressly permitted and/or requested by Pondera in writing, you may not copy, reproduce, retransmit, distribute, publish, commercially exploit or otherwise transfer any material from this Site or from the PVA; and </li>
                            <li>Pondera shall not be liable for any for any indirect, punitive, special, incidental, or consequential damages (including damages for loss of business, loss of data or profits or investment, or the like), in any way connected with the use of the PVA.</li>
                        </ol>
                        <p>Pondera prepares analyses and reports that may include aggregate or de-identified information about individuals who have completed the PVA assessment. We may share this information with third parties for marketing, advertising, research or similar purposes.  The information in these reports will not identify you individually.   Third parties who have access to these reports will not be able to contact you from the information contained in the reports.  We will not provide your personally identifiable information to any third parties without your consent.  See our <a onClick={(e) => this.togglePrivacy()}>Privacy Policy</a> for more details regarding our handling of information.</p>
                        <p>By clicking “I Accept” below, you are indicating that you understand and agree to these Terms and Condition of Use.</p>
                        <p>By clicking “I Decline” below, you are indicating that you do not wish to complete the assessment.</p>

                        <button onClick={this.acceptToS} className="btn btn-primary">I Accept</button>
                        <button onClick={this.declineToS} className="btn btn-default">I Decline</button>
                        <div style={{ 'clear': 'both' }} />
                    </div>
                    <div className="copyright-notice">© Pondera Technologies LLC {(new Date()).getFullYear()}</div>
                </div>);
        } else if (this.state.termsOfServiceDeclined) {
            // this is the decline page for terms of service
            result = (
                <div>
                    <img className="survey-logo" src="/images/ponderalogo.png" />
                    <div className="scale-question-wrapper question">
                        <h1>You have declined to complete the Pondera Profile</h1>

                        <p><strong>For more information, contact © Pondera Technologies LLC {(new Date()).getFullYear()}</strong></p>
                        <p><a href="mailto:support@ponderaadvisors.com">support@ponderaadvisors.com</a></p>
                        <p>To complete the on-line profile, please <a onClick={this.resetToS}>click here</a> and accept the terms and conditions.</p>
                    </div>
                    <div className="copyright-notice">© Pondera Technologies LLC {(new Date()).getFullYear()}</div>
                </div>);
        } else if (!this.state.demographicsSubmitted) {
            // this is the demographics
            const { demographicOptions } = this.state.survey;
            result = (
                <div>
                    <img className="survey-logo" src="/images/ponderalogo.png" />
                    <Demographics options={demographicOptions} handleSubmit={this.getDemographicsFromComponent} />
                    <div className="copyright-notice">© Pondera Technologies LLC {(new Date()).getFullYear()}</div>
                </div>);
        } else {
            // this is the actual assessment
            let count = 0;
            const { booleanQuestionCounter, scaleQuestionCounter, numberBooleanQuestions, numberScaleQuestions, booleanQuestions, scaleQuestions, numberTextQuestions, textQuestions, textQuestionCounter } = this.state;

            let instructionComponent, components;

            if (this.state.assessmentSection === AssessmentSection.TrueFalse) {
                instructionComponent = (
                    <div className="scale-question-wrapper question">
                        <h4>Section {binarySection} contains {numberBooleanQuestions} True/False items.</h4>
                        <p>Read each statement and decide whether or not it describes you. If you agree with a statement or decide that it describes you, respond True. If you disagree with a statement or decide that it does not describe you, respond False.</p>
                    </div>);

                components = booleanQuestions.slice(booleanQuestionCounter, booleanQuestionCounter + this.maxQuestionsPerPage).map(question => {
                    count++;

                    question.questionNumber = booleanQuestionCounter + count;

                    return <BinaryQuestion key={question.id} question={question} questionNumber={question.questionNumber} />;
                });
            } else if (this.state.assessmentSection === AssessmentSection.Likert) {
                instructionComponent = (
                    <div className="scale-question-wrapper question">
                        <h4>Section {likertSection} consists of {numberScaleQuestions} Likert scale items.</h4>
                        <p>This section consists of statements that provide you with an opportunity to describe yourself by indicating the degree to which each statement is true of the way you feel, think or act most of the time and in most situations. There are five possible responses to each sentence:</p>
                        <ol>
                            <li>Almost always not true or not true</li>
                            <li>Often not true</li>
                            <li>Sometimes</li>
                            <li>Often true</li>
                            <li>Almost always true or true</li>
                        </ol>
                    </div>);

                components = scaleQuestions.slice(scaleQuestionCounter, scaleQuestionCounter + this.maxQuestionsPerPage).map(question => {
                    count++;

                    question.questionNumber = scaleQuestionCounter + count;

                    return <ScaleQuestion key={question.id} question={question} questionNumber={question.questionNumber} />;
                });
            } else if (this.state.assessmentSection === AssessmentSection.Text) {
                instructionComponent = (
                    <div className="scale-question-wrapper question">
                        <h4>Section {textSection} consists of {numberTextQuestions} items.</h4>
                        <p>This section consists of statements that provide you with an opportunity to describe yourself by providing a free form text response.</p>
                    </div>);

                components = textQuestions.slice(textQuestionCounter, textQuestionCounter + this.maxQuestionsPerPage).map(question => {
                    count++;

                    question.questionNumber = textQuestionCounter + count;

                    return <TextQuestion key={question.id} question={question} questionNumber={question.questionNumber} />;
                });
            }

            progress = { width: (((booleanQuestionCounter + scaleQuestionCounter) / this.state.survey.questions.length) * 100.0) + '%' };

            var nextOrSubmit = <button className="btn btn-primary" onClick={this.next}>Next</button>;

            // we'll only be able to submit if the booleanQuestionCounter = the number of boolean questions
            // and likert + max page size is >= the number of likert questions
            if (this.showSubmitButton()) {
                nextOrSubmit = <button className="btn btn-primary" disabled={this.state.isSubmitting} onClick={this.submit}>Save</button>;
            }

            result = (
                <div>
                    <img className="survey-logo" src="/images/ponderalogo.png" onClick={(e: any) => this.handleKeyPress(e)} />
                    <div>
                        <div className="progress">
                            <div className="progress-bar progress-bar-striped" role="progressbar" aria-valuenow={50} aria-valuemin={0} aria-valuemax={100} style={progress} />
                        </div>
                        <div>
                            <div className="instructions">
                                {instructionComponent}
                            </div>
                            <div className="questions">
                                {components}
                            </div>
                            <div className="submit">
                                {nextOrSubmit}
                            </div>
                            <div style={{ 'clear': 'both' }} />
                        </div>
                    </div>
                    <div className="copyright-notice">© Pondera Technologies LLC {(new Date()).getFullYear()}</div>
                </div>);
        }

        const browser = detect();
        let browserCheck = (<div></div>);
        if (browser && browser.name && browser.name !== 'chrome' && browser.name !== 'firefox') {
            browserCheck = (<div className={"alert alert-danger alert-browser"}><strong>ATTENTION: </strong>It looks like you're using a browser that may be incompatible with our site. To ensure your assessment results are recorded correctly, please open this page in Firefox or Chrome.</div>);
        }

        return (
            <div className="Survey">
                <div className="bgBase" />
                <div className="bg" />
                <div className="bg2" />
                <div className="content">
                    <div className="container" ref={el => this.surveyWrapper = el}>
                        {browserCheck}
                        {result}
                    </div>
                    {this.state.isSubmitting && <LoadingIndicator showBackground={true} />}
                    <PrivacyPolicy modalVisible={this.state.showPrivacy} cb={(e: any) => this.togglePrivacy()} nolink={true} />
                </div>
            </div>);
    }

    private acceptAgreement() {
        this.setState({ agreementAccepted: true });
    }

    private submitDemographics() {
        this.setState({ demographicsSubmitted: true });
    }

    getDemographicsFromComponent = (responses: DemographicResponses) => {
        this.setState({ demographicResponses: responses });
        this.submitDemographics();
    }

    private acceptToS() {
        // this also triggers the start of binary testing
        this.setState({ 
            termsOfServiceAccepted: true,
            assessmentTimerData: {
                binaryStartDate: new Date(),
                binaryElapsedSeconds: 0,
                scaleElapsedSeconds: 0,
                textElapsedSeconds: 0
            },
            sectionStartTime: Date.now()
        });
    }

    private declineToS() {
        this.setState({ termsOfServiceDeclined: true });
    }

    private resetToS() {
        this.setState({ termsOfServiceDeclined: false });
    }

    private submit() {        
        const { booleanQuestions, scaleQuestions, textQuestions, assessmentTimerData } = this.state;

        const unansweredBooleanQuestions = this.checkAllQuestionsAnswered(booleanQuestions);
        const unansweredScaleQuestions = this.checkAllQuestionsAnswered(scaleQuestions);
        const unansweredTextQuestions = this.checkAllQuestionsAnswered(textQuestions);

        // make sure ALL questions are answered before submitting
        if (
            unansweredBooleanQuestions.length === 0 && 
            unansweredScaleQuestions.length === 0 && 
            unansweredTextQuestions.length === 0
        ) {
            // only modify assessment timers if the survey isn't complete
            if (!this.state.surveyCompleted) {
                if (this.state.assessmentSection === AssessmentSection.Text) {
                    assessmentTimerData!.textEndDate = new Date();

                    // update elapsed time for the scale section
                    let millis = Date.now() - this.state.sectionStartTime;
                    assessmentTimerData!.textElapsedSeconds += Math.floor(millis / 1000);
                } else if (this.state.assessmentSection === AssessmentSection.Likert) {
                    assessmentTimerData!.scaleEndDate = new Date();

                    // update elapsed time for the scale section
                    let millis = Date.now() - this.state.sectionStartTime;
                    assessmentTimerData!.scaleElapsedSeconds += Math.floor(millis / 1000);
                } else if (this.state.assessmentSection === AssessmentSection.TrueFalse) {
                    assessmentTimerData!.binaryEndDate = new Date();

                    // update elapsed time for the scale section
                    let millis = Date.now() - this.state.sectionStartTime;
                    assessmentTimerData!.binaryElapsedSeconds += Math.floor(millis / 1000);
                }
            }

            // we'll update state and save it into local storage, just in case something happens during submit
            this.setState(
                {
                    surveyCompleted: true,
                    isSubmitting: true,
                    assessmentTimerData: assessmentTimerData
                }, 
                this.saveToLocalStorage
            );

            this.surveyInstanceDataService.submit(this.state.survey, this.state.demographicResponses!, assessmentTimerData!)
                .then(() => {
                    // let's clear the progress data out of the users local storage
                    localStorage.removeItem(this.localStorageIdentifer);
                    this.props.history.push('/assessmentComplete');
                })
                .catch(() => {
                    this.props.notificationService.addError('Error submitting assessment data. Please try again');
                    this.setState({isSubmitting: true});
                });
        } else {
            // we should only need to display a messaged for unanswered text questions here as getting to this point in the survey requires you to answer everything on each page
            // and a page reload should put you back to the page of the first unanswered question it detects.
            this.displayUnansweredQuestionsAlert(unansweredTextQuestions);
        }
    }

    private checkAllQuestionsAnswered(questions: Question[]): number[] {
        // let allSet = true;

        const unansweredQuestions: number[] = [];

        questions.forEach(question => {
            if (question.questionType === 'text' || question.questionType === 'freeText') {
                if (!question.value) {
                    unansweredQuestions.push(question.questionNumber);
                    // allSet = false;
                }
            } else {
                var hasAnOptionSet = false;
                if (question.questionOptions) {
                    question.questionOptions.map(o => {
                        if (o.checked) {
                            hasAnOptionSet = true;
                        }
                    });
                }
                if (!hasAnOptionSet) {
                    unansweredQuestions.push(question.questionNumber);
                    // allSet = false;
                }
            }
        });

        return unansweredQuestions;

        // return allSet;
    }

    private displayUnansweredQuestionsAlert(unansweredQuestions: number[]) {
        if (unansweredQuestions.length > 0) {
            if (unansweredQuestions.length === 1) {
                alert(`A response for statement #${unansweredQuestions[0]} was not provided. Please go back and provide a response to continue.`);
            } else if (unansweredQuestions.length === 2) {
                alert(`Responses for statements #${unansweredQuestions[0]} and #${unansweredQuestions[1]} were not provided. Please go back and provide responses to continue.`);
            } else {
                let questionListText = '';

                unansweredQuestions.forEach((value, index, array) => {
                    if (index === 0) {
                        questionListText = `#${value}`;
                    } else if (index === array.length - 1) {
                        questionListText = `${questionListText}, and #${value}`;
                    } else {
                        questionListText = `${questionListText}, #${value}`;
                    } 
                });

                alert(`Responses for statements ${questionListText} were not provided. Please go back and provide responses to continue.`);
            }
        }
    }

    private findIndexOfLastAnswered(questions: Question[]) {
        let index = 0;

        for (let i = 0; i < questions.length; i++) {
            const question = questions[i];

            var hasAnOptionSet = false;
            if (question.questionOptions) {
                question.questionOptions.map(o => {
                    if (o.checked) {
                        hasAnOptionSet = true;
                    }
                });
            }
            if (!hasAnOptionSet) {
                index = i;
                break;
            }
        }

        return index;
    }

    private nextTrueFalse() {
        const { booleanQuestionCounter, numberBooleanQuestions, booleanQuestions } = this.state;

        if (booleanQuestionCounter >= numberBooleanQuestions) {
            return false;
        }

        // we're doing boolean questions
        let questions = booleanQuestions.slice(booleanQuestionCounter, booleanQuestionCounter + this.maxQuestionsPerPage);

        // const allSet = this.checkAllQuestionsAnswered(questions);
        const unansweredQuestions = this.checkAllQuestionsAnswered(questions);

        if (unansweredQuestions.length > 0) {
            this.displayUnansweredQuestionsAlert(unansweredQuestions);
            return true;
        }

        const newCount = booleanQuestionCounter + this.maxQuestionsPerPage;

        if (newCount < numberBooleanQuestions) {
            this.setState({
                booleanQuestionCounter: newCount
            });
        } else {
            // if we're here it means we've finished the boolean questions and are about to start the scale questions
            const newAssessmentTimerData = this.state.assessmentTimerData;

            // only modify assessment timers if the survey isn't complete
            if (!this.state.surveyCompleted) {
                newAssessmentTimerData!.binaryEndDate = new Date();
                newAssessmentTimerData!.scaleStartDate = new Date();
                newAssessmentTimerData!.textStartDate = new Date();

                // update elapsed time for the binary section
                let millis = Date.now() - this.state.sectionStartTime;
                newAssessmentTimerData!.binaryElapsedSeconds += Math.floor(millis / 1000);
            }

            this.setState({
                booleanQuestionCounter: numberBooleanQuestions,
                assessmentTimerData: newAssessmentTimerData,
                sectionStartTime: Date.now()
            });
            return false;
        }   

        this.setState({
            booleanQuestionCounter: newCount < numberBooleanQuestions ? newCount : numberBooleanQuestions
        });

        return true;
    }

    private nextLikert() {
        const { numberBooleanQuestions, scaleQuestionCounter, numberScaleQuestions, scaleQuestions } = this.state;

        if (scaleQuestionCounter >= numberScaleQuestions) {
            return false;
        }

        // we're doing scale questions
        let questions = scaleQuestions.slice(scaleQuestionCounter, scaleQuestionCounter + this.maxQuestionsPerPage);

        // const allSet = this.checkAllQuestionsAnswered(questions);
        const unansweredQuestions = this.checkAllQuestionsAnswered(questions);

        if (unansweredQuestions.length > 0) {
            this.displayUnansweredQuestionsAlert(unansweredQuestions);
            return true;
        }

        const newCount = scaleQuestionCounter + this.maxQuestionsPerPage;

        if (newCount < numberBooleanQuestions) {
            this.setState({
                scaleQuestionCounter: newCount < numberScaleQuestions ? newCount : numberScaleQuestions
            });
        } else {
            // if we're here it means we've finished the scale questions and are about to start the text questions
            const newAssessmentTimerData = this.state.assessmentTimerData;

            // only modify assessment timers if the survey isn't complete
            if (!this.state.surveyCompleted) {
                newAssessmentTimerData!.scaleEndDate = new Date();
                newAssessmentTimerData!.textStartDate = new Date();

                // update elapsed time for the scale section
                let millis = Date.now() - this.state.sectionStartTime;
                newAssessmentTimerData!.scaleElapsedSeconds += Math.floor(millis / 1000);
            }

            this.setState({
                scaleQuestionCounter: numberScaleQuestions,
                assessmentTimerData: newAssessmentTimerData,
                sectionStartTime: Date.now()
            });
            return false;
        }

        return true;
    }

    private nextText() {
        const { textQuestions, numberTextQuestions, textQuestionCounter } = this.state;

        // we're doing text questions
        let questions = textQuestions.slice(textQuestionCounter, textQuestionCounter + this.maxQuestionsPerPage);

        // const allSet = this.checkAllQuestionsAnswered(questions);
        const unansweredQuestions = this.checkAllQuestionsAnswered(questions);

        if (unansweredQuestions.length > 0) {
            this.displayUnansweredQuestionsAlert(unansweredQuestions);
            return true;
        }

        const newCount = textQuestionCounter + this.maxQuestionsPerPage;

        if (newCount < numberTextQuestions) {
            this.setState({
                textQuestionCounter: newCount < numberTextQuestions ? newCount : numberTextQuestions
            });
        } else {
            // if we're here it means we've finished the scale questions and are about to start the text questions
            const newAssessmentTimerData = this.state.assessmentTimerData;

            // only modify assessment timers if the survey isn't complete
            if (!this.state.surveyCompleted) {
                newAssessmentTimerData!.textEndDate = new Date();

                // update elapsed time for the scale section
                let millis = Date.now() - this.state.sectionStartTime;
                newAssessmentTimerData!.textElapsedSeconds += Math.floor(millis / 1000);
            }

            this.setState({
                textQuestionCounter: numberTextQuestions,
                assessmentTimerData: newAssessmentTimerData,
                sectionStartTime: Date.now()
            });
            return false;
        }

        return true;
    }

    private next() {
        if (this.state.assessmentSection === AssessmentSection.TrueFalse) {
            if (this.nextTrueFalse()) {
                return;
            } else {
                this.setState({
                    assessmentSection: AssessmentSection.Likert
                });
                return;
            }
        }

        if (this.state.assessmentSection === AssessmentSection.Likert) {
            if (this.nextLikert()) {
                return;
            } else {
                this.setState({
                    assessmentSection: AssessmentSection.Text
                });
                return;
            }
        }

        if (this.state.assessmentSection === AssessmentSection.Text && this.nextText()) {
            this.setState({
                assessmentSection: AssessmentSection.Text
            });
            return;
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/assessment/Assessment.tsx